import { z } from "zod";

import type { EcwEncounterRecency } from "@/src/lib/ehr/ecw/ecw-webecw-helpers";
import type { ReferralAuthOnFileSnapshot } from "@/src/lib/prior-authorization/referral-auth-on-file";
import type { StepOutputs } from "@/src/lib/workflows/steps/step-outputs";
import type { ReferralTelephoneEncounter } from "@/src/models/comms-escalation";
import type { Referral } from "@/src/models/documents/referral";
import type { Task } from "@/src/models/task";

import type { DateTime } from "luxon";

export enum WebecwTelEncNonCreationReason {
  NOT_NEEDED = "not_needed",
  UNKNOWN_CHART_ORIGIN = "unknown_chart_origin",
  EXISTING_TE = "existing_te",
  OTHER_REFERRAL = "other_referral",
  GUARD_UNAVAILABLE = "guard_unavailable",
  UNRESOLVED_ASSIGNEE = "unresolved_assignee",
  UNRESOLVED_DOCTOR = "unresolved_doctor",
  UNRESOLVED_FACILITY = "unresolved_facility",
  EXPLICIT_CREATE_FAILURE = "explicit_create_failure",
  UNCONFIRMED_CREATE_RESULT = "unconfirmed_create_result",
  AUTHORIZATION_ON_EXISTING_TE = "authorization_on_existing_te",
  MULTIPLE_APPEND_TARGETS = "multiple_append_targets",
  APPEND_UNCONFIRMED = "append_unconfirmed",
}

const WEBECW_TEL_ENC_EXISTING_TE_REASONS = [
  WebecwTelEncNonCreationReason.AUTHORIZATION_ON_EXISTING_TE,
  WebecwTelEncNonCreationReason.APPEND_UNCONFIRMED,
] as const;

export type WebecwTelEncExistingTeReason = (typeof WEBECW_TEL_ENC_EXISTING_TE_REASONS)[number];

const WEBECW_TEL_ENC_PLAIN_NON_CREATION_REASONS = [
  WebecwTelEncNonCreationReason.NOT_NEEDED,
  WebecwTelEncNonCreationReason.UNKNOWN_CHART_ORIGIN,
  WebecwTelEncNonCreationReason.EXISTING_TE,
  WebecwTelEncNonCreationReason.GUARD_UNAVAILABLE,
  WebecwTelEncNonCreationReason.UNRESOLVED_ASSIGNEE,
  WebecwTelEncNonCreationReason.UNRESOLVED_DOCTOR,
  WebecwTelEncNonCreationReason.UNRESOLVED_FACILITY,
  WebecwTelEncNonCreationReason.EXPLICIT_CREATE_FAILURE,
  WebecwTelEncNonCreationReason.UNCONFIRMED_CREATE_RESULT,
  WebecwTelEncNonCreationReason.MULTIPLE_APPEND_TARGETS,
] as const satisfies readonly Exclude<
  WebecwTelEncNonCreationReason,
  WebecwTelEncNonCreationReason.OTHER_REFERRAL | WebecwTelEncExistingTeReason
>[];

export type WebecwTelEncPlainNonCreationReason = (typeof WEBECW_TEL_ENC_PLAIN_NON_CREATION_REASONS)[number];

const webecwTelephoneEncounterIdentitySchema = z
  .object({ encounterId: z.string().trim().min(1), date: z.string().nullable(), reason: z.string() })
  .strict();

export type WebecwTelephoneEncounterIdentity = z.infer<typeof webecwTelephoneEncounterIdentitySchema>;

const webecwOtherReferralSchema = z
  .object({ taskId: z.string().trim().min(1), displayId: z.string().trim().min(1) })
  .strict();

export type WebecwOtherReferral = z.infer<typeof webecwOtherReferralSchema>;

export const webecwTelEncOutcomeSchema = z.union([
  z.object({ created: z.literal(true), encounterId: z.string().trim().min(1) }).strict(),
  z.discriminatedUnion("reason", [
    z.object({ created: z.literal(false), reason: z.enum(WEBECW_TEL_ENC_PLAIN_NON_CREATION_REASONS) }).strict(),
    z
      .object({
        created: z.literal(false),
        reason: z.literal(WebecwTelEncNonCreationReason.OTHER_REFERRAL),
        otherReferral: webecwOtherReferralSchema,
        telephoneEncounter: webecwTelephoneEncounterIdentitySchema.nullable(),
      })
      .strict(),
    z
      .object({
        created: z.literal(false),
        reason: z.enum(WEBECW_TEL_ENC_EXISTING_TE_REASONS),
        telephoneEncounter: webecwTelephoneEncounterIdentitySchema,
      })
      .strict(),
  ]),
]);

export type WebecwTelEncOutcome = z.infer<typeof webecwTelEncOutcomeSchema>;

export type WebecwTelephoneEncounterContent =
  | { appendable: true; telephoneEncounter: WebecwTelephoneEncounterIdentity; actionTaken: string; message: string }
  | { appendable: false; telephoneEncounter: WebecwTelephoneEncounterIdentity; actionTaken: string };

export type WebecwAppendableTelephoneEncounterContent = Extract<WebecwTelephoneEncounterContent, { appendable: true }>;

export enum WebecwTelephoneEncounterAppendDecisionKind {
  APPEND = "append",
  ALREADY_APPENDED = "already_appended",
  MULTIPLE_TARGETS = "multiple_targets",
  NO_TARGET = "no_target",
}

export type WebecwTelephoneEncounterAppendDecision =
  | {
      kind: WebecwTelephoneEncounterAppendDecisionKind.APPEND;
      target: WebecwAppendableTelephoneEncounterContent;
      text: string;
    }
  | {
      kind: WebecwTelephoneEncounterAppendDecisionKind.ALREADY_APPENDED;
      target: WebecwAppendableTelephoneEncounterContent;
    }
  | { kind: WebecwTelephoneEncounterAppendDecisionKind.MULTIPLE_TARGETS }
  | { kind: WebecwTelephoneEncounterAppendDecisionKind.NO_TARGET };

export type CreateWebecwTelEncOutput = { encounterId: string };

export type ReferralTelEncInstructionsOutput = { encounters: ReferralTelephoneEncounter[] };

export enum WebecwTelephoneEncounterDoctorSelectorKind {
  REFERRAL_NPI = "referral_npi",
  EXISTING_DOCTOR_ID = "existing_doctor_id",
}

export type WebecwTelephoneEncounterDoctorSelector =
  | {
      kind: WebecwTelephoneEncounterDoctorSelectorKind.REFERRAL_NPI;
      npi: string | null;
    }
  | {
      kind: WebecwTelephoneEncounterDoctorSelectorKind.EXISTING_DOCTOR_ID;
      doctorId: string | null;
    };

export interface BuildWebecwTelephoneEncounterDraftInput {
  task: Task;
  referral: Referral;
  created: boolean;
  encounters: readonly EcwEncounterRecency[];
  faxSenderNumber: string | null;
  readStepOutput: (key: keyof StepOutputs) => unknown;
  authOnFile: ReferralAuthOnFileSnapshot;
  at: DateTime;
}

export interface WebecwTelephoneEncounterDraft {
  reason: string;
  message: string;
  highPriority: boolean;
  doctorSelector: WebecwTelephoneEncounterDoctorSelector;
}

export interface WebecwReferralArrival {
  taskId: string;
  arrivedAt: DateTime;
  receivingProviderId: string | null;
}

export type WebecwReferralTelephoneEncounterStep =
  | { completed: false }
  | { completed: true; outcome: WebecwTelEncOutcome };

export interface WebecwReferralTeCandidate extends WebecwReferralArrival {
  displayId: string;
  processing: boolean;
  telephoneEncounterStep: WebecwReferralTelephoneEncounterStep;
}

export enum WebecwReferralTeOwnerKind {
  NONE = "none",
  HAS_TE = "has_te",
  STILL_RUNNING = "still_running",
}

export type WebecwReferralTeOwnerDecision =
  | { kind: WebecwReferralTeOwnerKind.NONE }
  | {
      kind: WebecwReferralTeOwnerKind.HAS_TE;
      otherReferral: WebecwOtherReferral;
      telephoneEncounter: WebecwTelephoneEncounterIdentity;
    }
  | { kind: WebecwReferralTeOwnerKind.STILL_RUNNING; otherReferral: WebecwOtherReferral };
